import React, { useState } from 'react';
import { Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { FaCamera } from 'react-icons/fa';
import { X } from 'lucide-react';

const CATEGORIES = [
  { value: 'platform_bug', label: 'Platform Bug (Website / App Issue)' },
  { value: 'institute_issue', label: 'Institute Issue' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const ReportIssueForm = ({ onSubmit, submitting, errorPayload, isTenantAdmin, institutes }) => {
  const [form, setForm] = useState({
    category: errorPayload ? 'platform_bug' : '',
    title: errorPayload ? `Error: ${errorPayload.message?.substring(0, 80)}` : '',
    description: errorPayload
      ? `**Auto-captured error**\n\n${errorPayload.message}\n\nStack:\n${errorPayload.stack || 'N/A'}`
      : '',
    priority: errorPayload ? 'high' : 'medium',
    onBehalfOfInstituteId: '',
  });
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [capturing, setCapturing] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (isTenantAdmin && !form.onBehalfOfInstituteId) errs.onBehalfOfInstituteId = 'Please select an institute';
    if (!form.category) errs.category = 'Please select a category';
    if (!form.title.trim()) errs.title = 'Title is required';
    if (!form.description.trim()) errs.description = 'Description is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScreenshot(file);
    const reader = new FileReader();
    reader.onload = () => setScreenshotPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCaptureScreenshot = async () => {
    try {
      setCapturing(true);
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: 0.8,
      });
      canvas.toBlob((blob) => {
        if (blob) {
          setScreenshot(new File([blob], 'screenshot.png', { type: 'image/png' }));
          setScreenshotPreview(canvas.toDataURL('image/png'));
        }
        setCapturing(false);
      }, 'image/png');
    } catch {
      setCapturing(false);
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({ ...form, screenshot });
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <Row className="g-3">
        {isTenantAdmin && (
          <Col xs={12}>
            <Form.Group>
              <Form.Label>Institute <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={form.onBehalfOfInstituteId}
                onChange={(e) => handleChange('onBehalfOfInstituteId', e.target.value)}
                isInvalid={!!errors.onBehalfOfInstituteId}
              >
                <option value="">Select institute...</option>
                {(institutes || []).map((inst) => (
                  <option key={inst._id} value={inst._id}>{inst.name}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.onBehalfOfInstituteId}</Form.Control.Feedback>
              <Form.Text className="text-muted">This ticket will be visible to the selected institute's admins.</Form.Text>
            </Form.Group>
          </Col>
        )}
        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label>Issue Category *</Form.Label>
            <Form.Select
              value={form.category}
              onChange={(e) => handleChange('category', e.target.value)}
              isInvalid={!!errors.category}
            >
              <option value="">Select category...</option>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.category}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label>Priority</Form.Label>
            <Form.Select
              value={form.priority}
              onChange={(e) => handleChange('priority', e.target.value)}
            >
              {PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Group>
            <Form.Label>Title *</Form.Label>
            <Form.Control
              type="text"
              placeholder="Brief summary of the issue"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              isInvalid={!!errors.title}
              maxLength={200}
            />
            <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Group>
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Describe the issue in detail..."
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              isInvalid={!!errors.description}
            />
            <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Group>
            <Form.Label>Screenshot (Optional)</Form.Label>
            <div className="d-flex gap-2 align-items-center">
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                size="sm"
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleCaptureScreenshot}
                disabled={capturing}
                className="d-flex align-items-center gap-1"
                type="button"
              >
                {capturing ? <Spinner size="sm" animation="border" /> : <FaCamera />}
                Capture
              </Button>
            </div>
          </Form.Group>
          {screenshotPreview && (
            <div className="report-modal__screenshot-preview">
              <img src={screenshotPreview} alt="Screenshot preview" />
              <button
                className="report-modal__screenshot-remove"
                onClick={removeScreenshot}
                type="button"
                aria-label="Remove screenshot"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </Col>
      </Row>

      <div className="report-modal__context-info">
        <span><strong>Page:</strong> {window.location.pathname}</span>
        <span><strong>Browser:</strong> {navigator.userAgent.match(/(?:Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/)?.[0] || 'Unknown'}</span>
        <span><strong>Resolution:</strong> {window.screen.width}x{window.screen.height}</span>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner size="sm" animation="border" className="me-1" />
              Submitting...
            </>
          ) : (
            'Submit Report'
          )}
        </Button>
      </div>
    </Form>
  );
};

export default ReportIssueForm;
